import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Theme,
  Typography
} from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { Container } from "@outschool/ui-legacy-component-library";
import { DeviceType, responsive } from "@outschool/ui-utils";
import lodashKebabCase from "lodash/kebabCase";
import React from "react";

import { headingOptions } from "../../components/contentful/ContentfulRenderOptions";
import ScrollToc from "./ScrollToc";

export default function AccordionPageContent({
  page,
  deviceType
}: {
  page: any;
  deviceType: DeviceType;
}) {
  const { t } = useTranslation(
    "client\\routes\\TermsPages\\AccordionPageContent"
  );
  const { showToc, sectionsCollection, preamble } = page;

  // Used in `Accordion` and `ScrollToc`
  const sections = sectionsCollection.items.map(
    ({ heading, body }: { heading: string; body: any }) => ({
      heading,
      id: lodashKebabCase(heading),
      body: documentToReactComponents(body?.json, headingOptions)
    })
  );

  return (
    <Container
      size="medium"
      sx={{
        marginY: responsive({ default: "large", medium: "xlarge" }),
        minHeight: "50vh"
      }}
    >
      <Box flex>
        {/* Sticky ToC */}
        {showToc && (
          <Box
            sx={{
              position: "sticky",
              top: "40px",
              overflow: "visible"
            }}
          >
            <ScrollToc
              label={t("On this page")}
              headings={sections}
              smoothScroll
              sx={(theme: Theme) => ({
                width: "300px",
                padding: "20px",
                borderRadius: "10px",
                marginRight: "40px",
                boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
                display: "none",
                [theme.breakpoints.up("md")]: {
                  display: "block"
                }
              })}
              activeSx={{
                backgroundColor: "warning.200"
              }}
              headingSx={{
                borderRadius: "10px",
                padding: "10px"
              }}
            />
          </Box>
        )}
        {/* Body */}
        <Box
          sx={{
            "& p": {
              marginBottom: "1em",
              whiteSpace: "pre-wrap"
            },

            "& ul": {
              padding: "revert"
            }
          }}
        >
          {/* Preamble */}
          {documentToReactComponents(preamble.json, headingOptions)}
          {/* Collapsible sections */}
          {sections.map((section: any, index: number) => (
            <Accordion
              id={section.id}
              key={index}
              sx={{
                h3: {
                  fontSize: "1.5em",
                  fontWeight: "500"
                },
                h4: {
                  fontSize: "1.5em"
                }
              }}
            >
              <AccordionSummary
                sx={{
                  fontSize: "2em",
                  fontWeight: "fontWeightMedium"
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: deviceType === "mobile" ? "0.6em" : "2em",
                    fontWeight: "fontWeightMedium"
                  }}
                >
                  {section.heading}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>{section.body}</AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>
    </Container>
  );
}
